
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, LogOut, User, CheckCircle2, Maximize, Minimize, ClipboardList, Map } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetHeader,
} from "@/components/ui/sheet";
import {
  CalendarDays,
  ListTodo,
  Users,
  FileText,
  Building,
  LayoutGrid,
  Activity,
  Info,
  Mail,
  LogIn,
  UserPlus,
  Contact,
  Send,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface MobileMenuProps {
  isLoggedIn: boolean;
  onToggleFullscreen?: () => void;
  isFullscreen?: boolean;
}

const MobileMenu = ({ isLoggedIn, onToggleFullscreen, isFullscreen }: MobileMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [username, setUsername] = useState<string>("");
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (isLoggedIn) {
      const getUsername = async () => {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('username')
            .eq('id', session.user.id)
            .maybeSingle();
          
          setUsername(profile?.username || session.user.email?.split('@')[0] || 'User');
        }
      };
      getUsername();
    }
  }, [isLoggedIn]);

  const handleLogout = async () => {
    try {
      window.localStorage.setItem('salesby:logout-intent-at', Date.now().toString());
      window.sessionStorage.setItem('salesby:logout-intent-at', Date.now().toString());
    } catch {}
    await supabase.auth.signOut();
    setIsOpen(false);
    toast({
      title: "Logged out",
      description: "You have been logged out successfully",
    });
    navigate("/login");
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" className="md:hidden px-0 text-foreground">
          <Menu className="h-6 w-6" />
          <span className="sr-only">Toggle menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[240px] sm:w-[280px]">
        {isLoggedIn && (
          <SheetHeader className="border-b pb-4 mb-4">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <span className="font-medium text-foreground">{username}</span>
            </div>
          </SheetHeader>
        )}
        <div className="py-2 flex flex-col space-y-2">
          {isLoggedIn ? (
            // Logged-in user menu
            <>
              <SheetClose asChild>
                <Link
                  to="/index"
                  className="flex items-center rounded-lg px-3 py-2 text-foreground bg-accent/10 hover:bg-accent/20 transition"
                >
                  <LayoutGrid className="mr-2 h-4 w-4" />
                  Dashboard
                </Link>
              </SheetClose>

              <SheetClose asChild>
                <Link
                  to="/stages"
                  className="flex items-center rounded-lg px-3 py-2 text-foreground hover:bg-accent/10 transition"
                >
                  <ListTodo className="mr-2 h-4 w-4" />
                  Pipeline
                </Link>
              </SheetClose>

              <SheetClose asChild>
                <Link
                  to="/weeks"
                  className="flex items-center rounded-lg px-3 py-2 text-foreground hover:bg-accent/10 transition"
                >
                  <CalendarDays className="mr-2 h-4 w-4" />
                  Weekly Log
                </Link>
              </SheetClose>

              <SheetClose asChild>
                <Link
                  to="/planning"
                  className="flex items-center rounded-lg px-3 py-2 text-foreground hover:bg-accent/10 transition"
                >
                  <ClipboardList className="mr-2 h-4 w-4" />
                  Planning
                </Link>
              </SheetClose>

              <SheetClose asChild>
                <Link
                  to="/prospecting"
                  className="flex items-center rounded-lg px-3 py-2 text-foreground hover:bg-accent/10 transition"
                >
                  <Activity className="mr-2 h-4 w-4" />
                  Prospecting
                </Link>
              </SheetClose>

              <SheetClose asChild>
                <Link
                  to="/trade-shows"
                  className="flex items-center rounded-lg px-3 py-2 text-foreground hover:bg-accent/10 transition"
                >
                  <Building className="mr-2 h-4 w-4" />
                  Trade Shows
                </Link>
              </SheetClose>

              <SheetClose asChild>
                <Link
                  to="/contacts"
                  className="flex items-center rounded-lg px-3 py-2 text-foreground hover:bg-accent/10 transition"
                >
                  <Contact className="mr-2 h-4 w-4" />
                  Contacts
                </Link>
              </SheetClose>

              <SheetClose asChild>
                <Link
                  to="/mapping"
                  className="flex items-center rounded-lg px-3 py-2 text-foreground hover:bg-accent/10 transition"
                >
                  <Map className="mr-2 h-4 w-4" />
                  Mapping
                </Link>
              </SheetClose>

              <SheetClose asChild>
                <Link
                  to="/reports"
                  className="flex items-center rounded-lg px-3 py-2 text-foreground hover:bg-accent/10 transition"
                >
                  <FileText className="mr-2 h-4 w-4" />
                  Reports
                </Link>
              </SheetClose>

              <SheetClose asChild>
                <Link
                  to="/communicator"
                  className="flex items-center rounded-lg px-3 py-2 text-foreground hover:bg-accent/10 transition"
                >
                  <Send className="mr-2 h-4 w-4" />
                  Communicator
                </Link>
              </SheetClose>


              <div className="pt-4 border-t mt-4 space-y-2">
                {onToggleFullscreen && (
                  <button
                    onClick={() => { onToggleFullscreen(); setIsOpen(false); }}
                    className="flex items-center rounded-lg px-3 py-2 text-foreground hover:bg-accent/10 transition w-full text-left text-sm"
                  >
                    {isFullscreen ? <Minimize className="mr-2 h-4 w-4" /> : <Maximize className="mr-2 h-4 w-4" />}
                    {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                  </button>
                )}
                <SheetClose asChild>
                  <a
                    href="https://www.salesby.us/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center rounded-lg px-3 py-2 text-foreground hover:bg-accent/10 transition text-sm"
                  >
                    <FileText className="mr-2 h-3 w-3" />
                    Privacy Policy
                  </a>
                </SheetClose>
                
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  className="w-full justify-start text-foreground hover:bg-accent/10"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </Button>
              </div>
            </>
          ) : (
            // Non-logged-in user menu
            <>
              <SheetClose asChild>
                <Link
                  to="/about"
                  className="flex items-center rounded-lg px-3 py-2 text-foreground hover:bg-accent/10 transition"
                >
                  <Info className="mr-2 h-4 w-4" />
                  About
                </Link>
              </SheetClose>

              <SheetClose asChild>
                <Link
                  to="/contact"
                  className="flex items-center rounded-lg px-3 py-2 text-foreground hover:bg-accent/10 transition"
                >
                  <Mail className="mr-2 h-4 w-4" />
                  Contact
                </Link>
              </SheetClose>

              <SheetClose asChild>
                <a
                  href="https://www.salesby.us/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center rounded-lg px-3 py-2 text-foreground hover:bg-accent/10 transition"
                >
                  <FileText className="mr-2 h-4 w-4" />
                  Privacy Policy
                </a>
              </SheetClose>

              <SheetClose asChild>
                <Link
                  to="/login"
                  className="flex items-center rounded-lg px-3 py-2 text-foreground hover:bg-accent/10 transition"
                >
                  <LogIn className="mr-2 h-4 w-4" />
                  Login
                </Link>
              </SheetClose>

              <SheetClose asChild>
                <Link
                  to="/register"
                  className="flex items-center rounded-lg px-3 py-2 text-foreground bg-accent/10 hover:bg-accent/20 transition"
                >
                  <UserPlus className="mr-2 h-4 w-4" />
                  Register
                </Link>
              </SheetClose>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MobileMenu;
